import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, ExternalLink, Users, Shield, Loader2 } from 'lucide-react'

interface TenantUser {
  id: number
  username: string
  email: string | null
  full_name: string | null
  role: string
  status: string
  created_at: string
  last_login_at: string | null
}

interface TenantManifest {
  id: string
  name: string
  operator: string
  operator_short: string
  domain: string
  branding: { primary: string; accent: string; logo: string }
  features: Record<string, boolean>
  deploy: { container: string; remote_path: string }
}

export function TenantDetailPage() {
  const { tenantId } = useParams<{ tenantId: string }>()
  const [tenant, setTenant] = useState<TenantManifest | null>(null)
  const [users, setUsers] = useState<TenantUser[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!tenantId) return
    fetch(`/api/portal/tenants/${tenantId}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.tenant) setTenant(data.tenant)
        if (data?.users) setUsers(data.users)
      })
      .finally(() => setLoading(false))
  }, [tenantId])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-brand-500" />
      </div>
    )
  }

  if (!tenant) {
    return (
      <div className="p-6">
        <p className="text-ink-soft">Tenant not found.</p>
        <Link to="/portal" className="text-brand-600 hover:underline text-sm mt-2 inline-block">
          Back to Portal
        </Link>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/portal" className="text-ink-soft hover:text-ink-dark transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
          style={{ backgroundColor: tenant.branding.primary }}
        >
          {tenant.operator_short?.[0] || tenant.name[0]}
        </div>
        <div>
          <h1 className="text-xl font-bold text-ink-dark">{tenant.name}</h1>
          <p className="text-sm text-ink-soft">{tenant.operator}</p>
        </div>
        <a
          href={`https://${tenant.domain}`}
          target="_blank"
          rel="noopener noreferrer"
          className="ml-auto text-sm font-medium text-brand-600 hover:underline flex items-center gap-1"
        >
          <ExternalLink className="w-4 h-4" />
          {tenant.domain}
        </a>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <InfoCard label="Container" value={tenant.deploy?.container || '—'} />
        <InfoCard label="Deploy Path" value={tenant.deploy?.remote_path || '—'} />
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-ink-dark flex items-center gap-2">
            <Users className="w-5 h-5" />
            Users ({users.length})
          </h2>
        </div>
        {users.length === 0 ? (
          <p className="text-sm text-ink-soft">No users registered for this copilot.</p>
        ) : (
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200/60 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-left text-xs text-ink-soft uppercase tracking-wider">
                  <th className="px-4 py-2.5">User</th>
                  <th className="px-4 py-2.5">Role</th>
                  <th className="px-4 py-2.5">Status</th>
                  <th className="px-4 py-2.5">Last Login</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} className="border-b border-slate-50 last:border-0">
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-ink-dark">{u.full_name || u.username}</div>
                      {u.email && <div className="text-xs text-ink-soft">{u.email}</div>}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                        u.role === 'admin'
                          ? 'bg-amber-50 text-amber-700'
                          : 'bg-slate-50 text-slate-600'
                      }`}>
                        {u.role === 'admin' && <Shield className="w-3 h-3" />}
                        {u.role}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`text-xs font-medium ${
                        u.status === 'active' ? 'text-green-600' : 'text-slate-400'
                      }`}>
                        {u.status || 'active'}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-ink-soft">
                      {u.last_login_at
                        ? new Date(u.last_login_at).toLocaleDateString()
                        : 'Never'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div>
        <Link
          to={`/portal/tenants/${tenant.id}/features`}
          className="inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-lg bg-brand-600 text-white hover:bg-brand-700 transition-colors"
        >
          Manage Feature Flags
        </Link>
      </div>
    </div>
  )
}

function InfoCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200/60 p-4">
      <div className="text-xs text-ink-soft uppercase tracking-wider">{label}</div>
      <div className="text-sm font-medium text-ink-dark mt-1 font-mono">{value}</div>
    </div>
  )
}
